import { ChangeDetectorRef, DestroyRef, Directive, inject, OnInit } from "@angular/core";

import { SearchQueryService } from "../../services/search";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FeedbackApiService } from "../../services/feedback-api.service";
import { SearchResultItem, SearchState } from "../../model";
import { SearchUtilsService } from "../../services/search/utils/search-utils.service";
import { BehaviorSubject, Observable } from "rxjs";

/**
 * Abstract base component for search results display
 * Contains all shared logic for search result handling, pagination, and state management
 */
@Directive()
export abstract class BaseSearchResultsComponent implements OnInit {
  protected searchQueryService = inject(SearchQueryService);

  private feedbackApiService = inject(FeedbackApiService);

  private destroyRef = inject(DestroyRef);

  private changeDetectorRef = inject(ChangeDetectorRef);

  public searchState: SearchState | null = null;

  private isLoadingMore = false;

  public readonly SearchUtilsService = SearchUtilsService;

  resultsSubject = new BehaviorSubject<SearchResultItem[]>([]);

  results$: Observable<SearchResultItem[]> = this.resultsSubject.asObservable();

  ngOnInit(): void {
    this.subscribeToSearchState();
  }

  /**
   * Subscribes to search state changes from the service
   */
  private subscribeToSearchState(): void {
    this.searchQueryService.searchState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.searchState = state;
      if (!state.loading) {
        this.isLoadingMore = false;
      }
      this.updateResults(state.results);
      this.changeDetectorRef.detectChanges();
    });
  }

  /** Call this when you get new results */
  updateResults(newResults: SearchResultItem[]): void {
    this.resultsSubject.next(newResults);
  }

  /**
   * Loads more results for the current search
   */
  loadMore(): void {
    this.searchQueryService.loadMoreResults().subscribe();
  }

  /**
   * Called when the infinite scroll panel enters the viewport.
   * Triggers loading more results if not already loading.
   */
  onInfiniteScroll(): void {
    // Avoid cancelling the in-flight control point score refinement with a load-more request.
    if (this.searchState?.loading || this.isLoadingMore || this.searchState?.scorePending) {
      return;
    }
    this.isLoadingMore = true;
    this.loadMore();
  }

  /**
   * Handles feedback action from result card
   * @param event The feedback event
   * @param index
   */
  onFeedback(event: { item: SearchResultItem; feedback: "up" | "down" | null }, index: number): void {
    if (event.feedback) {
      this.feedbackApiService
        .saveFeedbackImage(event.item.quadkey, this.searchState.query, index, event.feedback === "up" ? 1 : -1)
        .subscribe({
          next: (r) => console.log("Feedback saved", r),
          error: (e) => console.error("Error saving feedback", e),
        });
    } else {
      this.feedbackApiService.deleteFeedbackImage(event.item.quadkey, this.searchState.query, index).subscribe({
        next: (r) => console.log("Feedback deleted", r),
        error: (e) => console.error("Error deleting feedback", e),
      });
    }
  }

  /**
   * Track by function for search results
   * @param index Index of the item
   * @param item The search result item
   */
  trackByItem(index: number, item: SearchResultItem): string {
    return item.quadkey;
  }
}
